if (typeof MediaCarousel === 'undefined') {
  class MediaCarousel extends HTMLElement {
    constructor() {
      super();
      this.offsetX = 0;
      this.rafId = null;
      this.isDragging = false;
      this.isInertia = false;
      this.velocityX = 0;
      this.dragStartX = 0;
      this.dragStartOffset = 0;
      this.halfWidth = 0;
      this.velocitySamples = [];
      this.swiper = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.tick = this.tick.bind(this);

      this.inertiaFriction = 0.0025;
      this.inertiaMinVelocity = 20;
    }

    connectedCallback() {
      this.autoscroll = this.hasAttribute('data-autoscroll');
      this.speed = parseFloat(this.getAttribute('data-scroll-speed') || '40');

      if (this.autoscroll) {
        this.initContinuous();
      } else {
        this.initSwiper();
      }
    }

    disconnectedCallback() {
      if (this.autoscroll) {
        this.teardownContinuous();
      } else if (this.swiper) {
        this.swiper.destroy(true, false);
        this.swiper = null;
      }
    }

    /* ----------------------------- Continuous mode ---------------------------- */

    initContinuous() {
      this.viewport = this.querySelector('[data-media-carousel-viewport]');
      this.track = this.querySelector('[data-media-carousel-track]');
      if (!this.viewport || !this.track) return;

      this.setupClones();
      this.measure();
      this.bindContinuousEvents();
      this.start();

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.isOffscreen = !entry.isIntersecting;
        },
        { threshold: 0 }
      );
      this.intersectionObserver.observe(this);
    }

    teardownContinuous() {
      this.stop();
      this.unbindContinuousEvents();
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    setupClones() {
      if (this.track.dataset.cloned === 'true') return;

      const items = Array.from(this.track.children);
      if (items.length === 0) return;

      this.originalCount = items.length;

      const viewportWidth = this.viewport.offsetWidth || window.innerWidth;
      let contentWidth = this.measureSetWidth(items);

      while (contentWidth < viewportWidth * 2) {
        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.removeAttribute('data-media-carousel-item');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
          clone.querySelectorAll('a, button').forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
          this.track.appendChild(clone);
        });
        contentWidth += this.measureSetWidth(items);
        if (this.measureSetWidth(items) <= 0) break;
      }

      if (this.track.children.length === this.originalCount) {
        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.removeAttribute('data-media-carousel-item');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
          clone.querySelectorAll('a, button').forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
          this.track.appendChild(clone);
        });
      }

      this.track.dataset.cloned = 'true';
    }

    measureSetWidth(items) {
      if (!items.length) return 0;
      let width = 0;
      items.forEach((item) => {
        width += item.getBoundingClientRect().width;
      });
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      width += gap * items.length;
      return width;
    }

    measure() {
      const items = Array.from(this.track.querySelectorAll(':scope > [data-media-carousel-item]'));
      const originals =
        items.length > 0
          ? items.slice(0, this.originalCount || items.length)
          : Array.from(this.track.children).slice(0, this.originalCount || 0);

      if (!originals.length && this.originalCount) {
        const all = Array.from(this.track.children);
        this.halfWidth = this.measureSetWidth(all.slice(0, this.originalCount));
      } else if (originals.length) {
        this.originalCount = originals.length;
        this.halfWidth = this.measureSetWidth(originals);
      }

      this.normalizeOffset();
      this.applyTransform();
    }

    bindContinuousEvents() {
      window.addEventListener('resize', this.onResize);
      this.reducedMotion.addEventListener('change', this.onReducedMotionChange);

      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);

      this.viewport.addEventListener('mouseenter', () => {
        this.isHovered = true;
      });
      this.viewport.addEventListener('mouseleave', () => {
        this.isHovered = false;
      });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.track);
      }
    }

    unbindContinuousEvents() {
      window.removeEventListener('resize', this.onResize);
      this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
      if (this.viewport) {
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      }
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    onReducedMotionChange() {
      if (this.reducedMotion.matches) {
        this.isInertia = false;
        this.velocityX = 0;
        this.offsetX = 0;
        this.applyTransform();
      }
    }

    onResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.measure();
      }, 150);
    }

    onPointerDown(event) {
      if (event.button !== 0 && event.pointerType === 'mouse') return;

      this.isInertia = false;
      this.velocityX = 0;
      this.velocitySamples = [];

      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartOffset = this.offsetX;
      this.dragIntent = null;
      this.pointerActive = true;
      this.activePointerId = event.pointerId;
    }

    onPointerMove(event) {
      if (!this.pointerActive || event.pointerId !== this.activePointerId) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;

      if (this.dragIntent === null) {
        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
        this.dragIntent = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        if (this.dragIntent === 'y') {
          this.pointerActive = false;
          return;
        }
        this.isDragging = true;
        this.viewport.setPointerCapture?.(event.pointerId);
        this.viewport.style.cursor = 'grabbing';
      }

      if (!this.isDragging) return;
      event.preventDefault();

      const nextOffset = this.dragStartOffset + deltaX;
      const now = performance.now();

      this.velocitySamples.push({
        t: now,
        x: nextOffset,
      });
      while (this.velocitySamples.length > 6) {
        this.velocitySamples.shift();
      }

      this.offsetX = nextOffset;
      this.normalizeOffset();
      this.applyTransform();
    }

    onPointerUp(event) {
      if (event && this.activePointerId != null && event.pointerId !== this.activePointerId) {
        return;
      }
      this.pointerActive = false;
      this.activePointerId = null;
      this.dragIntent = null;
      if (!this.isDragging) return;

      this.isDragging = false;
      this.viewport.style.cursor = '';

      if (this.reducedMotion.matches) {
        this.velocityX = 0;
        return;
      }

      this.velocityX = this.getReleaseVelocity();
      this.velocitySamples = [];

      if (Math.abs(this.velocityX) >= this.inertiaMinVelocity) {
        this.isInertia = true;
      } else {
        this.velocityX = 0;
        this.isInertia = false;
      }
    }

    getReleaseVelocity() {
      const samples = this.velocitySamples;
      if (samples.length < 2) return 0;

      const newest = samples[samples.length - 1];
      let oldest = samples[0];
      for (let i = samples.length - 2; i >= 0; i--) {
        if (newest.t - samples[i].t > 100) {
          oldest = samples[i];
          break;
        }
        oldest = samples[i];
      }

      const dt = (newest.t - oldest.t) / 1000;
      if (dt <= 0) return 0;

      const raw = (newest.x - oldest.x) / dt;
      return Math.max(-3500, Math.min(3500, raw));
    }

    normalizeOffset() {
      if (!this.halfWidth) return;
      while (this.offsetX <= -this.halfWidth) {
        this.offsetX += this.halfWidth;
      }
      while (this.offsetX > 0) {
        this.offsetX -= this.halfWidth;
      }
    }

    applyTransform() {
      this.track.style.transform = `translate3d(${this.offsetX}px, 0, 0)`;
    }

    start() {
      if (this.rafId) return;
      this.lastTime = performance.now();
      this.rafId = requestAnimationFrame(this.tick);
    }

    stop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    tick(now) {
      const delta = Math.min((now - this.lastTime) / 1000, 0.064);
      this.lastTime = now;

      if (this.isInertia && this.halfWidth > 0) {
        this.offsetX += this.velocityX * delta;
        this.normalizeOffset();
        this.applyTransform();

        const decay = Math.exp(-this.inertiaFriction * 1000 * delta);
        this.velocityX *= decay;

        if (Math.abs(this.velocityX) < this.inertiaMinVelocity) {
          this.velocityX = 0;
          this.isInertia = false;
        }
      } else {
        const shouldAutoscroll =
          this.autoscroll &&
          !this.isDragging &&
          !this.isHovered &&
          !this.isOffscreen &&
          !this.reducedMotion.matches &&
          this.halfWidth > 0;

        if (shouldAutoscroll) {
          this.offsetX -= this.speed * delta;
          this.normalizeOffset();
          this.applyTransform();
        }
      }

      this.rafId = requestAnimationFrame(this.tick);
    }

    /* ------------------------------- Swiper mode ------------------------------ */

    initSwiper() {
      const swiperContainer = this.querySelector('.swiper');
      if (!swiperContainer || this.swiper) return;

      const gapDesktop = parseInt(this.getAttribute('data-gap') || '20', 10);
      const gapMobile = parseInt(this.getAttribute('data-gap-mobile') || '16', 10);

      whenReady('Swiper', () => {
        try {
          this.swiper = new Swiper(swiperContainer, {
            a11y: true,
            slidesPerView: 'auto',
            spaceBetween: gapMobile,
            loop: true,
            grabCursor: true,
            watchOverflow: true,
            mousewheel: {
              forceToAxis: true,
            },
            freeMode: {
              enabled: true,
              momentum: true,
              momentumRatio: 0.85,
              momentumVelocityRatio: 0.9,
            },
            breakpoints: {
              768: {
                spaceBetween: gapDesktop,
              },
            },
            modules: [Swiper.A11y, Swiper.Mousewheel, Swiper.FreeMode],
          });
        } catch (error) {
          console.error('Failed to initialize Media Carousel Swiper:', error);
        }
      });
    }

    /* --------------------------------- Shared --------------------------------- */

    scrollToItem(item) {
      if (!item) return;

      if (this.swiper) {
        const slides = Array.from(this.querySelectorAll('.swiper-slide'));
        const index = slides.indexOf(item);
        if (index >= 0) {
          this.swiper.slideToLoop
            ? this.swiper.slideToLoop(index)
            : this.swiper.slideTo(index);
        }
        return;
      }

      if (!this.halfWidth || !this.track) return;
      this.isInertia = false;
      this.velocityX = 0;
      const trackRect = this.track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const itemLeftInTrack = itemRect.left - trackRect.left - this.offsetX;
      this.offsetX = -itemLeftInTrack;
      this.normalizeOffset();
      this.applyTransform();
    }
  }

  if (!window.customElements.get('media-carousel')) {
    window.customElements.define('media-carousel', MediaCarousel);
  }
}
